import * as React from 'react';
import {LoaderCircle} from 'lucide-react';
import {Button as BaseButton, buttonVariants as baseButtonVariants} from '@/components/ui/button';
import {Hint} from '@/components/app/hint';
import {cn} from '@/lib/utils';

type BaseVariant = NonNullable<React.ComponentProps<typeof BaseButton>['variant']>;

// App variants are layered on a stock shadcn variant, so `components/ui/button.tsx` stays upgradeable.
const appVariants = {
  // Outline buttons sit on white cards; the stock variant uses the grey page background.
  outline: {base: 'outline', className: 'bg-card'},
  assistant: {base: 'default', className: 'bg-assistant text-assistant-foreground hover:bg-assistant-hover'},
  navigation: {
    base: 'ghost',
    className: 'bg-transparent text-navigation-foreground hover:bg-navigation-accent hover:text-navigation-foreground',
  },
  'destructive-outline': {
    base: 'outline',
    className:
      'border-destructive text-destructive hover:bg-destructive/10 hover:text-destructive focus-visible:ring-destructive/20',
  },
  'destructive-ghost': {
    base: 'ghost',
    className: 'text-destructive hover:bg-destructive/10 hover:text-destructive focus-visible:ring-destructive/20',
  },
  warning: {base: 'default', className: 'bg-amber-500 text-amber-950 hover:bg-amber-400'},
  'warning-ghost': {base: 'ghost', className: 'text-amber-700 hover:bg-amber-50 hover:text-amber-700'},
  success: {base: 'default', className: 'bg-green-700 text-white hover:bg-green-800'},
  'success-ghost': {base: 'ghost', className: 'text-green-700 hover:bg-green-50 hover:text-green-700'},
  // PrimeReact-style text button: primary-colored label without a surface.
  text: {base: 'ghost', className: 'text-primary hover:bg-primary/10 hover:text-primary'},
} satisfies Record<string, {base: BaseVariant; className: string}>;

export type ButtonVariant = BaseVariant | keyof typeof appVariants;
export type ButtonSize = NonNullable<React.ComponentProps<typeof BaseButton>['size']>;

function resolveVariant(variant: ButtonVariant = 'default') {
  const app = appVariants[variant as keyof typeof appVariants];
  return app ? {base: app.base as BaseVariant, className: app.className} : {base: variant as BaseVariant};
}

export function buttonVariants({
  variant,
  size,
  className,
}: {variant?: ButtonVariant; size?: ButtonSize; className?: string} = {}) {
  const resolved = resolveVariant(variant);
  return cn(baseButtonVariants({variant: resolved.base, size}), resolved.className, className);
}

export type ButtonProps = Omit<React.ComponentProps<typeof BaseButton>, 'variant'> & {
  variant?: ButtonVariant;
  isLoading?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  badge?: string | number;
  hint?: React.ReactNode;
};

/** Stock shadcn button plus the app's variants, icons, loading state, badge and tooltip. */
export function Button({
  className,
  variant = 'default',
  asChild = false,
  isLoading,
  startIcon,
  endIcon,
  badge,
  hint,
  children,
  disabled,
  type = 'button',
  ...props
}: ButtonProps) {
  const resolved = resolveVariant(variant);
  const element = (
    <BaseButton
      variant={resolved.base}
      className={cn(resolved.className, className)}
      data-variant={variant}
      asChild={asChild}
      disabled={disabled || isLoading}
      type={type}
      aria-busy={isLoading || undefined}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          {isLoading ? <LoaderCircle className="size-4 animate-spin" /> : startIcon}
          {children}
          {endIcon}
          {badge != null && (
            <span data-slot="button-badge" className="ml-1 rounded-full bg-background/20 px-1.5 text-xs">
              {badge}
            </span>
          )}
        </>
      )}
    </BaseButton>
  );
  return hint ? <Hint content={hint}>{element}</Hint> : element;
}
